import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import Input from "components/ui/Input";
import { getOpenInvoicesForClient } from "api/invoice.api";
import { getClientAvailableCredit } from "api/clientCreditApplication.api";

const PAYMENT_MODES = [
  { value: "Bank Deposit", icon: "Building2" },
  { value: "Cash", icon: "Banknote" },
  { value: "Cheque", icon: "FileCheck" },
  { value: "Credit Card", icon: "CreditCard" },
  { value: "Debit Card", icon: "CreditCard" },
  { value: "Demand Draft", icon: "FileText" },
  { value: "Online Payment Gateway", icon: "Globe" },
  { value: "Online Transfer", icon: "ArrowRightLeft" },
  { value: "PayPal", icon: "Wallet" },
  { value: "Wire Transfer", icon: "Zap" },
];

const NEEDS_DEPOSIT_TO = new Set([
  "Bank Deposit",
  "Online Transfer",
  "Wire Transfer",
  "PayPal",
]);

const NEEDS_ISSUER = new Set([
  "Cheque",
  "Demand Draft",
  "Credit Card",
  "Debit Card",
]);

const NEEDS_REFERENCE = new Set([
  "Bank Deposit",
  "Cheque",
  "Credit Card",
  "Debit Card",
  "Demand Draft",
  "Online Payment Gateway",
  "Online Transfer",
  "PayPal",
  "Wire Transfer",
]);

const EMPTY_FORM = {
  clientId: "",
  paymentDate: new Date().toISOString().split("T")[0],
  paymentMode: "Cash",
  amount: "",
  depositTo: "",
  issuer: "",
  reference: "",
  comments: "",
  allocations: [],
  creditApplications: [],
};

const money = (value, locale = "en-SA") =>
  Number(value || 0).toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const roundMoney = (value) => Number(Number(value || 0).toFixed(2));

const getRecordId = (record) => record?.id || record?._id || "";

const buildAutomaticCreditApplications = (
  invoices = [],
  availableCredit = 0,
) => {
  let remainingCredit = roundMoney(availableCredit);
  const applications = [];

  for (const invoice of invoices) {
    if (remainingCredit <= 0) break;

    const invoiceId = getRecordId(invoice);
    const invoiceBalance = Number(invoice.balanceDue || 0);
    const applyAmount = Math.min(invoiceBalance, remainingCredit);

    if (invoiceId && applyAmount > 0) {
      applications.push({
        invoiceId,
        amount: roundMoney(applyAmount),
      });

      remainingCredit = roundMoney(remainingCredit - applyAmount);
    }
  }

  return applications;
};

const getAmountForInvoice = (items = [], invoiceId) => {
  const item = items.find(
    (entry) => String(entry.invoiceId) === String(invoiceId),
  );

  return Number(item?.amount || 0);
};

const AddPaymentModal = ({
  existingPayment,
  clients = [],
  onSave,
  onClose,
  isSaving,
}) => {
  const { t, i18n } = useTranslation("pages");
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [openInvoices, setOpenInvoices] = useState([]);
  const [availableCredit, setAvailableCredit] = useState(0);
  const [isLoadingContext, setIsLoadingContext] = useState(false);
  const isEdit = !!existingPayment;
  const locale = i18n.language === "ar" ? "ar-SA" : "en-SA";

  useEffect(() => {
    if (existingPayment) {
      setForm({
        ...EMPTY_FORM,
        ...existingPayment,
        clientId: existingPayment.clientId || "",
        amount: existingPayment.amount ?? "",
        allocations: existingPayment.allocations || [],
        creditApplications: existingPayment.creditApplications || [],
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [existingPayment]);

  useEffect(() => {
    const loadClientContext = async () => {
      if (!form.clientId || isEdit) {
        setOpenInvoices([]);
        setAvailableCredit(0);
        return;
      }

      setIsLoadingContext(true);

      try {
        const [invoiceResponse, creditResponse] = await Promise.all([
          getOpenInvoicesForClient(form.clientId),
          getClientAvailableCredit(form.clientId),
        ]);

        const invoices =
          invoiceResponse?.invoices || invoiceResponse?.data?.invoices || [];

        const availableCreditAmount = Number(
          creditResponse?.availableCredit || 0,
        );

        setOpenInvoices(Array.isArray(invoices) ? invoices : []);
        setAvailableCredit(availableCreditAmount);

        setForm((prev) => ({
          ...prev,
          allocations: [],
          creditApplications: buildAutomaticCreditApplications(
            Array.isArray(invoices) ? invoices : [],
            availableCreditAmount,
          ),
        }));
      } catch {
        setOpenInvoices([]);
        setAvailableCredit(0);
      } finally {
        setIsLoadingContext(false);
      }
    };

    loadClientContext();
  }, [form.clientId, isEdit]);

  const creditAppliedAmount = useMemo(
    () =>
      roundMoney(
        (form.creditApplications || []).reduce(
          (sum, application) => sum + Number(application.amount || 0),
          0,
        ),
      ),
    [form.creditApplications],
  );

  const paymentAmount = Number(form.amount || 0);

  const allocatedAmount = useMemo(
    () =>
      roundMoney(
        (form.allocations || []).reduce(
          (sum, allocation) => sum + Number(allocation.amount || 0),
          0,
        ),
      ),
    [form.allocations],
  );

  const unappliedAmount = roundMoney(
    Math.max(0, paymentAmount - allocatedAmount),
  );

  const totalSettlementAmount = roundMoney(creditAppliedAmount + paymentAmount);

  const remainingCredit = roundMoney(
    Math.max(0, availableCredit - creditAppliedAmount),
  );

  const set = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleClientChange = (clientId) => {
    setForm((prev) => ({
      ...prev,
      clientId,
      amount: "",
      allocations: [],
      creditApplications: [],
    }));

    setErrors({});
  };

  const handleModeChange = (mode) => {
    setForm((prev) => ({
      ...prev,
      paymentMode: mode,
      depositTo: "",
      issuer: "",
      reference: "",
    }));
    setErrors({});
  };

  const setCreditApplicationAmount = (invoiceId, rawAmount) => {
    const amount = rawAmount === "" ? "" : Number(rawAmount);

    setForm((prev) => {
      const existing = prev.creditApplications || [];
      const withoutInvoice = existing.filter(
        (application) => String(application.invoiceId) !== String(invoiceId),
      );

      if (rawAmount === "" || amount <= 0 || Number.isNaN(amount)) {
        return {
          ...prev,
          creditApplications: withoutInvoice,
        };
      }

      return {
        ...prev,
        creditApplications: [
          ...withoutInvoice,
          {
            invoiceId,
            amount: roundMoney(amount),
          },
        ],
      };
    });
  };

  const setAllocationAmount = (invoiceId, rawAmount) => {
    const amount = rawAmount === "" ? "" : Number(rawAmount);

    setForm((prev) => {
      const existing = prev.allocations || [];
      const withoutInvoice = existing.filter(
        (allocation) => String(allocation.invoiceId) !== String(invoiceId),
      );

      if (rawAmount === "" || amount <= 0 || Number.isNaN(amount)) {
        return {
          ...prev,
          allocations: withoutInvoice,
        };
      }

      return {
        ...prev,
        allocations: [
          ...withoutInvoice,
          {
            invoiceId,
            amount: roundMoney(amount),
          },
        ],
      };
    });
  };

  const autoApplyCredit = () => {
    setForm((prev) => ({
      ...prev,
      creditApplications: buildAutomaticCreditApplications(
        openInvoices,
        availableCredit,
      ),
    }));
  };

  const autoAllocatePayment = () => {
    let remainingPayment = Number(form.amount || 0);

    if (remainingPayment <= 0) {
      setErrors((prev) => ({
        ...prev,
        amount: t("paymentModal.validation.autoAllocateAmount"),
      }));
      return;
    }

    const allocations = [];

    for (const invoice of openInvoices) {
      if (remainingPayment <= 0) break;

      const invoiceId = getRecordId(invoice);
      const invoiceBalance = Number(invoice.balanceDue || 0);
      const creditForInvoice = getAmountForInvoice(
        form.creditApplications,
        invoiceId,
      );

      const remainingInvoiceBalance = roundMoney(
        Math.max(0, invoiceBalance - creditForInvoice),
      );

      const allocationAmount = Math.min(
        remainingInvoiceBalance,
        remainingPayment,
      );

      if (invoiceId && allocationAmount > 0) {
        allocations.push({
          invoiceId,
          amount: roundMoney(allocationAmount),
        });

        remainingPayment = roundMoney(remainingPayment - allocationAmount);
      }
    }

    setForm((prev) => ({
      ...prev,
      allocations,
    }));
  };

  const validate = () => {
    const e = {};

    if (!form.clientId) {
      e.clientId = t("paymentModal.validation.clientRequired");
    }

    if (!form.paymentDate) {
      e.paymentDate = t("paymentModal.validation.paymentDateRequired");
    }

    if (Number.isNaN(paymentAmount) || paymentAmount < 0) {
      e.amount = t("paymentModal.validation.paymentPositive");
    }

    if (paymentAmount <= 0 && creditAppliedAmount <= 0) {
      e.amount = t("paymentModal.validation.paymentOrCreditRequired");
    }

    if (creditAppliedAmount > availableCredit) {
      e.creditApplications = t("paymentModal.validation.creditExceeds");
    }

    if (allocatedAmount > paymentAmount) {
      e.allocations = t("paymentModal.validation.allocationExceeds");
    }

    for (const invoice of openInvoices) {
      const invoiceId = getRecordId(invoice);
      const invoiceBalance = Number(invoice.balanceDue || 0);
      const creditForInvoice = getAmountForInvoice(
        form.creditApplications,
        invoiceId,
      );
      const paymentForInvoice = getAmountForInvoice(
        form.allocations,
        invoiceId,
      );

      if (creditForInvoice > invoiceBalance) {
        e.creditApplications = t("paymentModal.validation.creditExceedsInvoice", {
          invoiceNumber: invoice.invoiceNumber,
        });
      }

      if (creditForInvoice + paymentForInvoice > invoiceBalance) {
        e.allocations = t(
          "paymentModal.validation.paymentPlusCreditExceeds",
          { invoiceNumber: invoice.invoiceNumber },
        );
      }
    }

    return e;
  };

  const handleSave = () => {
    const e = validate();

    if (Object.keys(e).length > 0) {
      setErrors(e);
      return;
    }

    onSave({
      ...(isEdit ? { id: existingPayment.id } : {}),
      clientId: form.clientId,
      paymentDate: form.paymentDate,
      paymentMode: form.paymentMode,
      amount: roundMoney(paymentAmount),
      allocations: (form.allocations || []).filter(
        (item) => item.invoiceId && Number(item.amount || 0) > 0,
      ),
      creditApplications: (form.creditApplications || []).filter(
        (item) => item.invoiceId && Number(item.amount || 0) > 0,
      ),
      depositTo: form.depositTo.trim() || null,
      issuer: form.issuer.trim() || null,
      reference: form.reference.trim() || null,
      comments: form.comments.trim() || null,
    });
  };

  const selectedClient = clients.find(
    (client) => String(client.id || client._id) === String(form.clientId),
  );

  const needsDepositTo = NEEDS_DEPOSIT_TO.has(form.paymentMode);
  const needsIssuer = NEEDS_ISSUER.has(form.paymentMode);
  const needsReference = NEEDS_REFERENCE.has(form.paymentMode);
  const isCash = form.paymentMode === "Cash";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0, y: 16 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.95, opacity: 0, y: 16 }}
        transition={{ duration: 0.22 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-border w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-border flex items-center justify-between bg-muted/30 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Wallet" size={18} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                {isEdit
                  ? t("paymentModal.titleEdit")
                  : t("paymentModal.titleAdd")}
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                {isEdit
                  ? t("paymentModal.subtitleEdit")
                  : t("paymentModal.subtitleAdd")}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={18} className="text-muted-foreground" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t("paymentModal.fields.client")}{" "}
                <span className="text-red-500">*</span>
              </label>

              <select
                value={form.clientId}
                disabled={isEdit}
                onChange={(event) => handleClientChange(event.target.value)}
                className={`w-full px-3 py-2 border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent ${
                  errors.clientId ? "border-red-500" : "border-border"
                }`}
              >
                <option value="">{t("paymentModal.placeholders.selectClient")}</option>
                {clients.map((client) => (
                  <option
                    key={client.id || client._id}
                    value={client.id || client._id}
                  >
                    {client.name}
                  </option>
                ))}
              </select>

              {errors.clientId && (
                <p className="text-xs text-red-500 mt-1">{errors.clientId}</p>
              )}

              {selectedClient && (
                <p className="text-xs text-muted-foreground mt-1">
                  {t("paymentModal.selected", { name: selectedClient.name })}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t("paymentModal.fields.paymentDate")}{" "}
                <span className="text-red-500">*</span>
              </label>
              <Input
                type="date"
                value={form.paymentDate}
                onChange={(e) => set("paymentDate", e.target.value)}
                className={errors.paymentDate ? "border-red-500" : ""}
              />
              {errors.paymentDate && (
                <p className="text-xs text-red-500 mt-1">
                  {errors.paymentDate}
                </p>
              )}
            </div>
          </div>

          {!isEdit && form.clientId && availableCredit > 0 && (
            <div className="rounded-xl border border-green-200 bg-green-50 dark:bg-green-900/10 dark:border-green-800 p-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-green-800 dark:text-green-300">
                    {t("paymentModal.existingCreditTitle")}
                  </h3>
                  <p className="text-xs text-green-700 dark:text-green-400 mt-1">
                    {t("paymentModal.existingCreditDescription")}
                  </p>
                </div>

                <div className="text-end">
                  <div className="text-xs text-green-700 dark:text-green-400">
                    {t("paymentModal.availableCredit")}
                  </div>
                  <div className="text-lg font-bold text-green-700 dark:text-green-300">
                    SR {money(availableCredit, locale)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {!isEdit && form.clientId && (
            <div className="border border-border rounded-xl p-4 bg-muted/20">
              <div className="flex items-center justify-between gap-3 mb-3">
                <div>
                  <h3 className="text-sm font-semibold text-foreground">
                    {t("paymentModal.settleTitle")}
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    {t("paymentModal.helpers.settle")}
                  </p>
                </div>

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={autoApplyCredit}
                    disabled={
                      isLoadingContext ||
                      openInvoices.length === 0 ||
                      availableCredit <= 0
                    }
                  >
                    {t("paymentModal.actions.applyMaxCredit")}
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={autoAllocatePayment}
                    disabled={isLoadingContext || openInvoices.length === 0}
                  >
                    {t("paymentModal.actions.autoAllocatePayment")}
                  </Button>
                </div>
              </div>

              {isLoadingContext ? (
                <p className="text-sm text-muted-foreground">
                  {t("paymentModal.loadingContext")}
                </p>
              ) : openInvoices.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {t("paymentModal.noOpenInvoices")}
                </p>
              ) : (
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {openInvoices.map((invoice) => {
                    const invoiceId = getRecordId(invoice);
                    const creditAmount = getAmountForInvoice(
                      form.creditApplications,
                      invoiceId,
                    );
                    const paymentAllocation = getAmountForInvoice(
                      form.allocations,
                      invoiceId,
                    );
                    const invoiceBalance = Number(invoice.balanceDue || 0);
                    const remainingAfterCredit = roundMoney(
                      Math.max(0, invoiceBalance - creditAmount),
                    );

                    return (
                      <div
                        key={invoiceId}
                        className="grid grid-cols-1 md:grid-cols-[1fr_140px_140px] gap-3 items-center p-3 rounded-lg border border-border bg-background"
                      >
                        <div>
                          <div className="font-medium text-sm text-foreground">
                            {invoice.invoiceNumber}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {t("paymentModal.invoice.balance")}: SR{" "}
                            {money(invoice.balanceDue, locale)} -{" "}
                            {t("paymentModal.invoice.remainingAfterCredit")}: SR{" "}
                            {money(remainingAfterCredit, locale)}
                          </div>
                        </div>

                        <div>
                          <label className="block text-[11px] text-muted-foreground mb-1">
                            {t("paymentModal.fields.applyCredit")}
                          </label>
                          <Input
                            type="number"
                            min="0"
                            max={Math.min(invoiceBalance, availableCredit)}
                            step="0.01"
                            placeholder={t("paymentModal.placeholders.amount")}
                            value={creditAmount || ""}
                            onChange={(e) =>
                              setCreditApplicationAmount(
                                invoiceId,
                                e.target.value,
                              )
                            }
                          />
                        </div>

                        <div>
                          <label className="block text-[11px] text-muted-foreground mb-1">
                            {t("paymentModal.fields.newPayment")}
                          </label>
                          <Input
                            type="number"
                            min="0"
                            max={remainingAfterCredit}
                            step="0.01"
                            placeholder={t("paymentModal.placeholders.amount")}
                            value={paymentAllocation || ""}
                            onChange={(e) =>
                              setAllocationAmount(invoiceId, e.target.value)
                            }
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4 text-sm">
                <div className="p-3 rounded-lg bg-background border border-border">
                  <div className="text-muted-foreground">
                    {t("paymentModal.availableCredit")}
                  </div>
                  <div className="font-semibold">
                    SR {money(availableCredit, locale)}
                  </div>
                </div>

                <div className="p-3 rounded-lg bg-background border border-border">
                  <div className="text-muted-foreground">
                    {t("paymentModal.creditApplied")}
                  </div>
                  <div className="font-semibold text-green-600">
                    SR {money(creditAppliedAmount, locale)}
                  </div>
                </div>

                <div className="p-3 rounded-lg bg-background border border-border">
                  <div className="text-muted-foreground">
                    {t("paymentModal.creditLeft")}
                  </div>
                  <div className="font-semibold">
                    SR {money(remainingCredit, locale)}
                  </div>
                </div>

                <div className="p-3 rounded-lg bg-background border border-border">
                  <div className="text-muted-foreground">
                    {t("paymentModal.fields.newPayment")}
                  </div>
                  <div className="font-semibold">
                    SR {money(paymentAmount, locale)}
                  </div>
                </div>

                <div className="p-3 rounded-lg bg-background border border-border">
                  <div className="text-muted-foreground">
                    {t("paymentModal.totalSettlement")}
                  </div>
                  <div className="font-semibold">
                    SR {money(totalSettlementAmount, locale)}
                  </div>
                </div>
              </div>

              {errors.creditApplications && (
                <p className="text-xs text-red-500 mt-2">
                  {errors.creditApplications}
                </p>
              )}

              {errors.allocations && (
                <p className="text-xs text-red-500 mt-2">
                  {errors.allocations}
                </p>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t("paymentModal.fields.amount")}
            </label>

            <div className="relative">
              <span className="absolute start-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground font-medium pointer-events-none">
                SR
              </span>
              <Input
                type="number"
                value={form.amount}
                onChange={(e) => set("amount", e.target.value)}
                placeholder={t("paymentModal.placeholders.amount")}
                min="0"
                step="0.01"
                className={`ps-9 ${errors.amount ? "border-red-500" : ""}`}
              />
            </div>

            <p className="text-xs text-muted-foreground mt-1">
              {t("paymentModal.helpers.amount")}
            </p>

            {errors.amount && (
              <p className="text-xs text-red-500 mt-1">{errors.amount}</p>
            )}
          </div>

          {paymentAmount > 0 && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-3">
                {t("paymentModal.fields.paymentMode")}{" "}
                <span className="text-red-500">*</span>
              </label>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                {PAYMENT_MODES.map((mode) => (
                  <button
                    key={mode.value}
                    type="button"
                    onClick={() => handleModeChange(mode.value)}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border text-xs font-medium transition-all duration-150 ${
                      form.paymentMode === mode.value
                        ? "border-primary bg-primary/10 text-primary shadow-sm"
                        : "border-border text-muted-foreground hover:border-primary/40 hover:text-foreground hover:bg-muted/50"
                    }`}
                  >
                    <Icon
                      name={mode.icon}
                      size={18}
                      className={
                        form.paymentMode === mode.value
                          ? "text-primary"
                          : "text-muted-foreground"
                      }
                    />
                    <span className="text-center leading-tight">
                      {t(`paymentModes.${mode.value}`)}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <AnimatePresence>
            {paymentAmount > 0 && !isCash && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.25 }}
                className="space-y-4 overflow-hidden"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {needsDepositTo && (
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        {t("paymentModal.fields.depositTo")}
                      </label>
                      <Input
                        type="text"
                        value={form.depositTo}
                        onChange={(e) => set("depositTo", e.target.value)}
                        placeholder={t("paymentModal.placeholders.depositTo")}
                      />
                    </div>
                  )}

                  {needsIssuer && (
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        {t("paymentModal.fields.issuer")}
                      </label>
                      <Input
                        type="text"
                        value={form.issuer}
                        onChange={(e) => set("issuer", e.target.value)}
                        placeholder={
                          form.paymentMode === "Cheque" ||
                          form.paymentMode === "Demand Draft"
                            ? t("paymentModal.placeholders.bank")
                            : t("paymentModal.placeholders.card")
                        }
                      />
                    </div>
                  )}

                  {needsReference && (
                    <div
                      className={
                        needsDepositTo || needsIssuer ? "" : "md:col-span-2"
                      }
                    >
                      <label className="block text-sm font-medium text-foreground mb-2">
                        {t("paymentModal.fields.reference", {
                          type:
                            form.paymentMode === "Cheque" ||
                            form.paymentMode === "Demand Draft"
                              ? t("paymentModal.referenceTypes.cheque")
                              : t("paymentModal.referenceTypes.transaction"),
                        })}
                      </label>
                      <Input
                        type="text"
                        value={form.reference}
                        onChange={(e) => set("reference", e.target.value)}
                        placeholder={
                          form.paymentMode === "Cheque" ||
                          form.paymentMode === "Demand Draft"
                            ? t("paymentModal.placeholders.voucherNumber")
                            : t("paymentModal.placeholders.transactionId")
                        }
                      />
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t("paymentModal.fields.comments")}
            </label>
            <textarea
              value={form.comments}
              onChange={(e) => set("comments", e.target.value)}
              placeholder={t("paymentModal.placeholders.comments")}
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent resize-none text-sm"
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-border bg-muted/30 flex items-center justify-between flex-shrink-0">
          {totalSettlementAmount > 0 ? (
            <div className="text-sm text-muted-foreground">
              {t("paymentModal.summary.applying")}{" "}
              <span className="font-semibold text-green-600">
                SR {money(creditAppliedAmount, locale)}
              </span>{" "}
              {t("paymentModal.summary.credit")}
              {paymentAmount > 0 && (
                <>
                  {" "}
                  + {t("paymentModal.summary.recording")}{" "}
                  <span className="font-semibold text-foreground">
                    SR {money(paymentAmount, locale)}
                  </span>{" "}
                  {t("paymentModal.summary.via")}{" "}
                  <span className="font-medium text-foreground">
                    {t(`paymentModes.${form.paymentMode}`)}
                  </span>
                </>
              )}
            </div>
          ) : (
            <div />
          )}

          <div className="flex gap-3">
            <Button variant="outline" size="md" onClick={onClose}>
              {t("common:actions.close")}
            </Button>
            <Button
              variant="default"
              size="md"
              iconName={isEdit ? "Save" : "Wallet"}
              onClick={handleSave}
              disabled={isSaving}
            >
              {isEdit
                ? t("paymentModal.actions.saveChanges")
                : t("paymentModal.actions.saveAndSettle")}
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default AddPaymentModal;
